import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { env } from '@/config/env';

type PortalType = 'admin' | 'business' | 'consumer';

interface UseRedirectReturn {
  portalType: PortalType | null;
  tenantId: string | null;
  redirectUrl: string | null;
  /**
   * Resolve the destination URL and navigate. Async so it can await an
   * inline `verifyCustomDomain` call when `useVerifiedDomains` hasn't
   * populated the state yet (race-safe path for white-label tenants on
   * custom domains).
   */
  redirect: (token: string) => Promise<void>;
  /**
   * Synchronous variant — uses only `useVerifiedDomains` state. May
   * fall back to the default portal URL if the verification effect
   * hasn't completed yet. Prefer `redirect()` for actual navigation;
   * use this when you need a URL string and can tolerate the race
   * (e.g. for display/preview).
   */
  getRedirectUrl: (token: string) => string;
  /**
   * Race-safe variant of getRedirectUrl. If `verifiedDomains` state is
   * empty AND the redirect URL is on a non-platform domain, awaits an
   * inline `verifyCustomDomain` lookup before deciding. Use this when
   * the white-label tenant path matters.
   */
  getRedirectUrlAsync: (token: string) => Promise<string>;
}

// Portal URLs from validated environment config (no fallbacks)
const DEFAULT_PORTAL_URLS: Record<PortalType, string> = {
  admin: env.VITE_ADMIN_PORTAL_URL,
  business: env.VITE_BUSINESS_PORTAL_URL,
  consumer: env.VITE_CONSUMER_PORTAL_URL,
};

// Platform domains — always allowed for redirects
const PLATFORM_REDIRECT_DOMAINS = ['localhost', '127.0.0.1', 'opsmatrix.dev', 'fiops.ai'];

const EMPTY_DOMAIN_SET: ReadonlySet<string> = new Set();

/** Check if a hostname belongs to a known platform domain (sync, always safe). */
export function isPlatformDomain(hostname: string): boolean {
  return PLATFORM_REDIRECT_DOMAINS.some((d) => hostname === d || hostname.endsWith(`.${d}`));
}

/**
 * Validates a redirect URL is safe.
 *
 * Allows:
 * - Platform domains (*.fiops.ai, *.opsmatrix.dev, localhost)
 * - Verified custom domain subdomains (portal.*, app.*) — only if the base
 *   domain has been verified against the API as a registered custom domain.
 *
 * Blocks: arbitrary domains, javascript: URLs, data: URLs, unverified custom domains.
 *
 * SECURITY: Custom domains are NOT allowed by prefix alone. The base domain
 * must be resolved via /website/resolve-domain to prevent open redirect attacks
 * (e.g., portal.attacker.com would pass a prefix-only check).
 */
export function isValidRedirectUrl(url: string, verifiedCustomDomains?: ReadonlySet<string>): boolean {
  // Defense-in-depth: block protocol-relative URLs before parsing
  if (url.startsWith('//')) return false;

  try {
    const parsed = new URL(url);

    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
      return false;
    }

    // Platform domains — always allowed
    if (isPlatformDomain(parsed.hostname)) {
      return true;
    }

    // Custom domain portal subdomains — only allowed if the base domain is
    // verified as a registered custom domain on our platform.
    if (
      parsed.protocol === 'https:' &&
      verifiedCustomDomains &&
      (parsed.hostname.startsWith('portal.') || parsed.hostname.startsWith('app.'))
    ) {
      const baseDomain = parsed.hostname.replace(/^(portal|app)\./, '');
      return verifiedCustomDomains.has(baseDomain);
    }

    return false;
  } catch {
    return false;
  }
}

/**
 * Resolve a hostname against the platform's custom domain registry.
 * Returns the base domain if it's registered, or null if not.
 */
export async function verifyCustomDomain(hostname: string): Promise<string | null> {
  // Only check portal.* and app.* subdomains
  if (!hostname.startsWith('portal.') && !hostname.startsWith('app.')) return null;
  const baseDomain = hostname.replace(/^(portal|app)\./, '');
  if (!baseDomain.includes('.')) return null; // Must be a real domain

  try {
    const response = await fetch(`${env.VITE_API_URL}/api/v1/website/resolve-domain`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ hostname: baseDomain }),
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { businessId?: string };
    return data?.businessId ? baseDomain : null;
  } catch {
    return null;
  }
}

/**
 * Pre-verifies a redirect URL's custom domain against the platform registry.
 * Skips the network call for platform domains (already trusted) and non-portal subdomains.
 */
export function useVerifiedDomains(rawUrl: string | null): ReadonlySet<string> {
  const [verifiedDomains, setVerifiedDomains] = useState<ReadonlySet<string>>(EMPTY_DOMAIN_SET);

  useEffect(() => {
    let cancelled = false;
    if (!rawUrl) return undefined;
    try {
      const parsed = new URL(rawUrl);
      if (isPlatformDomain(parsed.hostname)) return undefined;

      verifyCustomDomain(parsed.hostname).then((domain) => {
        if (cancelled) return;
        if (domain) setVerifiedDomains(new Set([domain]));
      });
    } catch {
      // Invalid URL — isValidRedirectUrl will reject it
    }
    return () => {
      cancelled = true;
    };
  }, [rawUrl]);

  return verifiedDomains;
}

export function useRedirect(): UseRedirectReturn {
  const [searchParams] = useSearchParams();

  const portalType = searchParams.get('portal') as PortalType | null;
  const tenantId = searchParams.get('tenant');
  // Honor `?redirect=`, `?returnUrl=`, and `?next=` (industry convention used
  // by Django/Rails/marketing email links). Same isValidRedirectUrl validation
  // applies to all three — adding `?next=` is purely a fallback so deep-links
  // with the conventional param name don't silently lose their target.
  const redirectUrl = searchParams.get('redirect') || searchParams.get('returnUrl') || searchParams.get('next');
  const verifiedDomains = useVerifiedDomains(redirectUrl);

  const getRedirectUrl = useCallback(
    (token: string): string => {
      // If a redirect URL was provided and is valid, use it
      if (redirectUrl && isValidRedirectUrl(redirectUrl, verifiedDomains)) {
        const separator = redirectUrl.includes('?') ? '&' : '?';
        return `${redirectUrl}${separator}token=${token}`;
      }

      // Otherwise, use the default URL for the portal type
      const baseUrl = DEFAULT_PORTAL_URLS[portalType ?? 'business'];
      return `${baseUrl}?token=${token}`;
    },
    [redirectUrl, portalType, verifiedDomains],
  );

  /**
   * Race-safe URL resolution. When `verifiedDomains` state hasn't been
   * populated yet (e.g. registration completed before
   * `verifyCustomDomain()` returned), perform the verification inline
   * before deciding whether to honor the redirect.
   *
   * Without this, white-label tenants on custom domains fall through to
   * the platform default if the user clicks Submit fast enough — the
   * sync `getRedirectUrl` reads `verifiedDomains = EMPTY_DOMAIN_SET` and
   * `isValidRedirectUrl` returns false for the custom domain.
   *
   * Refs: PR #1574 round-3 reviewer comments 3180740592, 3180740613,
   * 3180740651.
   */
  const getRedirectUrlAsync = useCallback(
    async (token: string): Promise<string> => {
      if (!redirectUrl) {
        const baseUrl = DEFAULT_PORTAL_URLS[portalType ?? 'business'];
        return `${baseUrl}?token=${token}`;
      }

      // Try the sync state first — common case when the verification effect
      // has had time to run (most flows take seconds, the verify is fast).
      if (isValidRedirectUrl(redirectUrl, verifiedDomains)) {
        const separator = redirectUrl.includes('?') ? '&' : '?';
        return `${redirectUrl}${separator}token=${token}`;
      }

      // Sync path rejected — but if the URL is a non-platform `portal.*` /
      // `app.*` subdomain, the rejection might just mean the verify hasn't
      // resolved. Run it inline.
      try {
        const parsed = new URL(redirectUrl);
        if (
          !isPlatformDomain(parsed.hostname) &&
          (parsed.hostname.startsWith('portal.') || parsed.hostname.startsWith('app.'))
        ) {
          const verifiedBase = await verifyCustomDomain(parsed.hostname);
          if (verifiedBase) {
            const inlineSet: ReadonlySet<string> = new Set([verifiedBase]);
            if (isValidRedirectUrl(redirectUrl, inlineSet)) {
              const separator = redirectUrl.includes('?') ? '&' : '?';
              return `${redirectUrl}${separator}token=${token}`;
            }
          }
        }
      } catch {
        // Invalid URL — fall through to default portal
      }

      const baseUrl = DEFAULT_PORTAL_URLS[portalType ?? 'business'];
      return `${baseUrl}?token=${token}`;
    },
    [redirectUrl, portalType, verifiedDomains],
  );

  const redirect = useCallback(
    async (token: string): Promise<void> => {
      const url = await getRedirectUrlAsync(token);
      window.location.href = url;
    },
    [getRedirectUrlAsync],
  );

  return {
    portalType,
    tenantId,
    redirectUrl,
    redirect,
    getRedirectUrl,
    getRedirectUrlAsync,
  };
}

export default useRedirect;
